import React from 'react';
import { Flex, Text, Button, Box } from '@chakra-ui/react';
import { bookingFlowStore } from '../../store/BookingFlowStore';
import { authStore } from '../../store/AuthStore';
import FamilyMemberStep from '../FamilyMemberStep';
import { observer } from 'mobx-react-lite';
import { servicebookingFlowStore } from '../../store/ServiceBookingFlowStore';
import { useAlert } from '../AlertProvider';
import { patientStore } from '../../store/PatientStore';

interface Step1_UserSelectionProps {
  selectMe: () => void;
  onSelectFamilyMember: (member: any) => void;
  onOpenCreateFamilyMemberModal: () => void;
}

const Step1_UserSelection: React.FC<Step1_UserSelectionProps> = observer(({
  selectMe,
  onSelectFamilyMember,
  onOpenCreateFamilyMemberModal,
}) => {
  const isLoggedIn = authStore.isLoggedInSync;
  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  const showFamilyMemberStep = isServiceBooking ? servicebookingFlowStore.showFamilyMemberStep : bookingFlowStore.showFamilyMemberStep;
  const { showAlert: showPopUpAlert } = useAlert();
  
  // Get current patient from the store - this will be reactive with observer
  const currentPatient = patientStore.currentPatient;
  
  // Access observable values within the rendered component (reactive context)
  const currentBookingStep = bookingFlowStore.currentStep;
  const currentServiceStep = servicebookingFlowStore.currentStep;
  const isNewPatient = currentPatient?.is_new_pt ?? "1";
  const appointmentTypeNewPatient = servicebookingFlowStore.appointmentData?.appointmentType?.new_patient;

  // Extract the validation logic to a function
  const isAppointmentTypeNotForNewPatient = () => {
    return isLoggedIn && 
           appointmentTypeNewPatient === "0" && 
           isNewPatient === "1";
  };

  const handleForMyselfClick = () => {
    if (isAppointmentTypeNotForNewPatient()) {
      showPopUpAlert({
        severity: "warning",
        title: "This Appointment type not for new Patients",
        message: "Then please select another appointment type.",
        onClose: () => {
          servicebookingFlowStore.setIsModalOpen(false);
        },
      });
      return;
    }
    
    // Use values from the component scope instead of accessing stores directly
    // This avoids the "outside reactive context" warning
    
    // Update both stores with the "For myself" selection and advance to next step
    bookingFlowStore.updateAppointmentData("isForUser", true);
    bookingFlowStore.nextStep();
    bookingFlowStore.setActiveStep(currentBookingStep - 1);
    
    servicebookingFlowStore.updateAppointmentData("isForUser", true);
    servicebookingFlowStore.nextStep();
    servicebookingFlowStore.setActiveStep(currentServiceStep - 1);
    
    selectMe();
  };

  const handleForFamilyMemberClick = () => {
    bookingFlowStore.updateAppointmentData("isForUser", false);
    servicebookingFlowStore.updateAppointmentData("isForUser", false);

    if (isLoggedIn) {
      bookingFlowStore.setShowFamilyMemberStep(true);
      servicebookingFlowStore.setShowFamilyMemberStep(true);
    } else {
      // Use values from the component scope
      bookingFlowStore.nextStep();
      servicebookingFlowStore.nextStep();
      bookingFlowStore.setActiveStep(currentBookingStep - 1);
      servicebookingFlowStore.setActiveStep(currentServiceStep - 1);
    }
  };

  return (
    <Flex flexDirection="column" alignItems="center" justifyContent="center" width="100%">
      {!showFamilyMemberStep && (
        <>
          <Text fontSize="lg" fontWeight="bold" mb={4}>Who is this appointment for?</Text>
          <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }}>
            <Button
              variant="outline"
              borderColor={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.inputBorder : "#B92F2F"}
              color={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
              border="2px"
              _hover={{
                bg: isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F",
                color: "#fff",
                border:"2px",
                borderColor: isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"
              }}
              onClick={handleForFamilyMemberClick}
            >
              For a family member
            </Button>

            <Button
              bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
              color="white"
              _hover={{ bg: isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "red.700" }}
              onClick={handleForMyselfClick}
            >
              For myself
            </Button>
          </Flex>
        </>
      )}

      {/* Render Family Member selector when user is logged in and chose family member */}
      {isLoggedIn && showFamilyMemberStep && (
        <Box mt={6}>
          <Text fontSize="lg" fontWeight="bold" mb={4}>Select your Family Member</Text>
          <FamilyMemberStep
            onSelectFamilyMember={onSelectFamilyMember}
            onOpenCreateFamilyMemberModal={onOpenCreateFamilyMemberModal}
          />
        </Box>
      )}
    </Flex>
  );
});

export default Step1_UserSelection;